import { supabase } from './supabaseClient'
import { audioService } from './audioService'

export const transcriptionService = {
  // 创建转录任务
  async createTranscription(audioFile, userId) {
    try {
      // 获取音频时长
      let duration = null
      try {
        const durationSeconds = await audioService.getAudioDuration(audioFile)
        duration = `${Math.floor(durationSeconds)} seconds`
      } catch (error) {
        console.warn('无法获取音频时长:', error)
      }

      // 上传音频文件
      const uploadResult = await audioService.uploadAudioFile(audioFile, userId)

      // 创建转录记录
      const { data, error } = await supabase
        .from('audio_transcriptions')
        .insert({
          user_id: userId,
          audio_file_path: uploadResult.path,
          original_filename: uploadResult.fileName,
          file_size: uploadResult.fileSize,
          duration: duration,
          status: 'pending'
        })
        .select()
        .single()

      if (error) throw error

      return data
    } catch (error) {
      console.error('创建转录任务失败:', error)
      throw error
    }
  },

  // 开始转录处理
  async startTranscription(transcriptionId, audioFilePath) {
    try {
      const { data, error } = await supabase.functions.invoke('transcribe-audio', {
        body: {
          transcriptionId,
          audioFilePath
        }
      })

      if (error) throw error
      return data
    } catch (error) {
      console.error('启动转录失败:', error)
      throw error
    }
  },

  // 获取用户的转录记录
  async getUserTranscriptions(userId, limit = 20, offset = 0) {
    try {
      const { data, error } = await supabase
        .from('audio_transcriptions')
        .select(`
          id,
          original_filename,
          file_size,
          duration,
          status,
          speaker_count,
          created_at,
          completed_at
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1)

      if (error) throw error
      return data
    } catch (error) {
      console.error('获取转录记录失败:', error)
      throw error
    }
  },

  // 获取转录详情（包含说话者片段）
  async getTranscriptionDetails(transcriptionId) {
    try {
      // 获取转录基本信息
      const { data: transcription, error: transcriptionError } = await supabase
        .from('audio_transcriptions')
        .select('*')
        .eq('id', transcriptionId)
        .single()

      if (transcriptionError) throw transcriptionError

      // 获取说话者片段
      const { data: segments, error: segmentsError } = await supabase
        .from('speaker_segments')
        .select('*')
        .eq('transcription_id', transcriptionId)
        .order('start_time', { ascending: true })

      if (segmentsError) throw segmentsError

      return {
        ...transcription,
        segments: segments || []
      }
    } catch (error) {
      console.error('获取转录详情失败:', error)
      throw error
    }
  },

  // 删除转录记录
  async deleteTranscription(transcriptionId, audioFilePath) {
    try {
      // 删除数据库记录（级联删除会自动删除相关的speaker_segments）
      const { error: dbError } = await supabase
        .from('audio_transcriptions')
        .delete()
        .eq('id', transcriptionId)

      if (dbError) throw dbError

      // 删除音频文件
      if (audioFilePath) {
        await audioService.deleteAudioFile(audioFilePath)
      }
    } catch (error) {
      console.error('删除转录记录失败:', error)
      throw error
    }
  },



  // 下载转录结果
  downloadTranscription(transcription, format = 'txt') {
    const content = this.exportAsText(transcription)
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    
    const link = document.createElement('a')
    link.href = url
    link.download = `${transcription.original_filename}_转录结果.${format}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  },

  // 保存用户编辑的文本
  async saveEditedText(transcriptionId, editedText) {
    try {
      const { data, error } = await supabase
        .from('audio_transcriptions')
        .update({
          edited_text: editedText,
          is_edited: true,
          edited_at: new Date().toISOString()
        })
        .eq('id', transcriptionId)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('保存编辑文本失败:', error)
      throw error
    }
  },

  // 获取显示文本（优先显示编辑版本）
  getDisplayText(transcription) {
    return transcription.edited_text || transcription.formatted_text || '暂无转录内容'
  },

  // 导出转录结果为文本（包含编辑版本）
  exportAsText(transcription) {
    const { original_filename, formatted_text, edited_text, speaker_count, created_at, is_edited, edited_at } = transcription
    
    let content = `转录文件: ${original_filename}\n`
    content += `转录时间: ${new Date(created_at).toLocaleString()}\n`
    content += `说话者数量: ${speaker_count}\n`
    
    if (is_edited && edited_at) {
      content += `编辑时间: ${new Date(edited_at).toLocaleString()}\n`
      content += `状态: 已校对\n`
    }
    
    content += `\n${'='.repeat(50)}\n\n`
    content += this.getDisplayText(transcription)
    
    return content
  },

  // 实时监听转录状态变化
  subscribeToTranscriptionUpdates(transcriptionId, callback) {
    const subscription = supabase
      .channel(`transcription-${transcriptionId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'audio_transcriptions',
          filter: `id=eq.${transcriptionId}`
        },
        (payload) => {
          callback(payload.new)
        }
      )
      .subscribe()

    return subscription
  }
}